import { Link, usePage } from '@inertiajs/react';

import PageHero from '@/components/public/sections/PageHero';
import Seo from '@/components/public/Seo';
import Section from '@/components/public/ui/Section';
import { parishName, parishEyebrow, useSite } from '@/hooks/useSite';
import { PARISH_TIME_ZONE } from '@/lib/datetime';

type Gallery = {
  id: number;
  title: string;
  slug: string;
  description: string | null;
  publishedAt: string | null;
  eventTitle: string;
  eventStartsAt: string | null;
  eventLocation: string | null;
  itemCount: number;
  coverUrl: string | null;
};

type Props = {
  galleries: Gallery[];
  [key: string]: unknown;
};

const dateFormatter = new Intl.DateTimeFormat('pt-PT', {
  dateStyle: 'long',
  timeZone: PARISH_TIME_ZONE,
});

function formatDate(isoString: string | null): string | null {
  if (!isoString) return null;
  return dateFormatter.format(new Date(isoString));
}

export default function GalleriesIndex() {
  const { galleries } = usePage<Props>().props;
  const site = useSite();

  return (
    <>
      <Seo
        title="Galerias"
        description={`Fotografias e memórias dos eventos da ${parishName(site)}.`}
      />
      <PageHero
        badge="Memórias da comunidade"
        title="Galerias"
        accentWord="fotográficas"
        subtitle={`Reveja momentos de encontros, iniciativas culturais, assembleias e atividades que marcaram a vida da ${parishEyebrow(site).toLowerCase()}.`}
      />

      <Section spacing="md" aria-labelledby="galleries-heading">
        <h2 id="galleries-heading" className="sr-only">
          Lista de galerias
        </h2>
        {galleries.length === 0 ? (
          <div className="surface-card p-12 text-center">
            <p className="text-on-surface-light text-lg">Ainda não existem galerias disponíveis.</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 gap-6 md:grid-cols-2 lg:grid-cols-3">
            {galleries.map((gallery) => (
              <Link
                key={gallery.id}
                href={`/galleries/${gallery.slug}`}
                className="surface-card group overflow-hidden transition-transform duration-200 hover:-translate-y-1"
              >
                <div className="bg-background border-line aspect-[16/10] border-b">
                  {gallery.coverUrl ? (
                    <img
                      src={gallery.coverUrl}
                      alt=""
                      className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105"
                      loading="lazy"
                    />
                  ) : (
                    <div className="text-on-surface-light/50 flex h-full items-center justify-center">
                      <span className="material-symbols-outlined text-5xl">photo_library</span>
                    </div>
                  )}
                </div>
                <div className="space-y-3 p-5">
                  <div>
                    <p className="text-secondary text-[11px] font-bold tracking-[0.14em] uppercase">
                      {gallery.eventTitle}
                    </p>
                    <h3 className="font-headline text-on-surface mt-1.5 text-2xl leading-tight font-semibold">
                      {gallery.title}
                    </h3>
                  </div>
                  {gallery.description && (
                    <p className="text-on-surface-light line-clamp-2 text-sm leading-relaxed">
                      {gallery.description}
                    </p>
                  )}
                  <div className="text-on-surface-light flex flex-wrap items-center gap-x-3 gap-y-1 text-xs">
                    {formatDate(gallery.eventStartsAt) && (
                      <span>{formatDate(gallery.eventStartsAt)}</span>
                    )}
                    {gallery.eventLocation && <span>{gallery.eventLocation}</span>}
                    <span>{gallery.itemCount} itens</span>
                  </div>
                </div>
              </Link>
            ))}
          </div>
        )}
      </Section>
    </>
  );
}
